import { useMemo, useState } from "react";
import { CheckCircle2, AlertCircle } from "lucide-react";
import PrimaryButton from "../../ui/PrimaryButton";

const GOOGLE_SCRIPT_URL = process.env.REACT_APP_GOOGLE_SHEETS_WEB_APP_URL || "";

const initialValues = {
  enquirerRole: "",
  studentName: "",
  phoneNumber: "",
  email: "",
  interestArea: "",
  preferredStudyPath: "",
  counsellingMode: "",
  message: ""
};

function ContactForm({
  enquirerRoles = [],
  interestAreas = [],
  preferredStudyPaths = [],
  counsellingModes = []
}) {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [statusMessage, setStatusMessage] = useState("");

  const phonePattern = useMemo(() => /^[0-9+\s-]{8,15}$/, []);

  const validate = () => {
    const nextErrors = {};

    if (!values.enquirerRole) nextErrors.enquirerRole = "Select who is filling this enquiry.";
    if (!values.studentName.trim()) nextErrors.studentName = "Student name is required.";
    if (!values.phoneNumber.trim()) {
      nextErrors.phoneNumber = "Phone number is required.";
    } else if (!phonePattern.test(values.phoneNumber.trim())) {
      nextErrors.phoneNumber = "Enter a valid phone number.";
    }

    if (!values.email.trim()) {
      nextErrors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
      nextErrors.email = "Enter a valid email address.";
    }

    if (!values.interestArea) nextErrors.interestArea = "Select an interest area.";
    if (!values.preferredStudyPath) nextErrors.preferredStudyPath = "Select a preferred study path.";
    if (!values.counsellingMode) nextErrors.counsellingMode = "Select a preferred counselling mode.";
    if (!values.message.trim()) nextErrors.message = "Please share a brief message.";

    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus("idle");
    setStatusMessage("");

    const validationErrors = validate();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      setStatus("error");
      setStatusMessage("Please review the highlighted fields and try again.");
      return;
    }

    if (!GOOGLE_SCRIPT_URL) {
      setStatus("error");
      setStatusMessage("Form integration is not configured. Add REACT_APP_GOOGLE_SHEETS_WEB_APP_URL to continue.");
      return;
    }

    setStatus("submitting");

    const payload = new URLSearchParams({
      name: values.studentName.trim(),
      phone: values.phoneNumber.trim(),
      email: values.email.trim(),
      course: values.preferredStudyPath || values.interestArea || "",
      message: [
        `Role: ${values.enquirerRole}`,
        `Interest Area: ${values.interestArea}`,
        `Preferred Study Path: ${values.preferredStudyPath}`,
        `Counselling Mode: ${values.counsellingMode}`,
        `Message: ${values.message.trim()}`
      ].join("\n"),
      source: "thecareerspace-contact-form"
    });

    try {
      const response = await fetch(GOOGLE_SCRIPT_URL, {
        method: "POST",
        body: payload
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      let result = { ok: true };
      try {
        result = await response.json();
      } catch (parseError) {
        // Some Apps Script deployments may return plain text; treat 2xx as success.
      }

      if (result?.ok === false) {
        throw new Error(result.error || "Google Sheets endpoint reported a failure.");
      }

      setStatus("success");
      setStatusMessage("Thank you. Your enquiry was submitted successfully.");
      setValues(initialValues);
      setErrors({});
    } catch (error) {
      setStatus("error");
      setStatusMessage(error?.message || "We could not submit your enquiry right now. Please try again.");
      console.error("Contact form submission failed:", error);
    }
  };

  const inputClassName =
    "mt-2 w-full rounded-xl border border-brand-line/80 bg-brand-sky/35 px-3.5 py-3 text-sm text-brand-navy placeholder:text-brand-charcoal/55 shadow-sm focus:border-brand-gold-strong focus:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/35";

  return (
    <form
      id="enquiry-form"
      onSubmit={handleSubmit}
      className="overflow-hidden rounded-[26px] border border-brand-line/80 bg-gradient-to-b from-white via-white to-brand-sky/35 shadow-[0_30px_60px_-36px_rgba(10,31,68,0.45)]"
      noValidate
    >
      <div className="border-b border-brand-line/70 bg-brand-navy px-6 py-5 text-white sm:px-7">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-gold-soft">
          Admissions Enquiry
        </p>
        <h3 className="mt-2 text-3xl font-semibold leading-tight text-white">Send an enquiry</h3>
        <p className="mt-2 text-sm leading-relaxed text-white/90">
          Share your details for a counselling-first discussion. Our team uses this to plan a practical first call.
        </p>
      </div>

      <div className="p-6 sm:p-7">
        <fieldset>
          <legend className="text-xs font-semibold uppercase tracking-[0.12em] text-brand-charcoal/86">
            I am filling this enquiry as
          </legend>
          <div className="mt-3 flex flex-wrap gap-2">
          {enquirerRoles.map((role) => {
            const isActive = values.enquirerRole === role;

            return (
              <button
                key={role}
                type="button"
                onClick={() => setValues((prev) => ({ ...prev, enquirerRole: role }))}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.1em] transition ${
                  isActive
                    ? "border-brand-gold-strong bg-brand-gold text-brand-navy shadow-sm"
                    : "border-brand-line bg-white text-brand-charcoal hover:border-brand-gold/55 hover:bg-brand-gold-soft/45 hover:text-brand-navy"
                }`}
              >
                {role}
              </button>
            );
          })}
          </div>
          {errors.enquirerRole ? <span className="mt-1 block text-xs text-red-600">{errors.enquirerRole}</span> : null}
        </fieldset>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <label className="text-xs font-semibold uppercase tracking-[0.08em] text-brand-charcoal/86">
            Student name
            <input
              type="text"
              value={values.studentName}
              onChange={(event) => setValues((prev) => ({ ...prev, studentName: event.target.value }))}
              className={inputClassName}
              placeholder="Enter student name"
            />
            {errors.studentName ? <span className="mt-1 block text-xs text-red-600">{errors.studentName}</span> : null}
          </label>

          <label className="text-xs font-semibold uppercase tracking-[0.08em] text-brand-charcoal/86">
            Phone number
            <input
              type="tel"
              value={values.phoneNumber}
              onChange={(event) => setValues((prev) => ({ ...prev, phoneNumber: event.target.value }))}
              className={inputClassName}
              placeholder="Enter phone number"
            />
            <span className="mt-1 block text-[11px] font-medium text-brand-charcoal/70">
              Use the preferred callback number.
            </span>
            {errors.phoneNumber ? <span className="mt-1 block text-xs text-red-600">{errors.phoneNumber}</span> : null}
          </label>

          <label className="text-xs font-semibold uppercase tracking-[0.08em] text-brand-charcoal/86">
            Email
            <input
              type="email"
              value={values.email}
              onChange={(event) => setValues((prev) => ({ ...prev, email: event.target.value }))}
              className={inputClassName}
              placeholder="Enter email address"
            />
            {errors.email ? <span className="mt-1 block text-xs text-red-600">{errors.email}</span> : null}
          </label>

          <label className="text-xs font-semibold uppercase tracking-[0.08em] text-brand-charcoal/86">
            Interest area
            <select
              value={values.interestArea}
              onChange={(event) => setValues((prev) => ({ ...prev, interestArea: event.target.value }))}
              className={inputClassName}
            >
              <option value="">Select interest area</option>
              {interestAreas.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {errors.interestArea ? <span className="mt-1 block text-xs text-red-600">{errors.interestArea}</span> : null}
          </label>

          <label className="text-xs font-semibold uppercase tracking-[0.08em] text-brand-charcoal/86">
            Preferred study path
            <select
              value={values.preferredStudyPath}
              onChange={(event) => setValues((prev) => ({ ...prev, preferredStudyPath: event.target.value }))}
              className={inputClassName}
            >
              <option value="">Select preferred path</option>
              {preferredStudyPaths.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {errors.preferredStudyPath ? (
              <span className="mt-1 block text-xs text-red-600">{errors.preferredStudyPath}</span>
            ) : null}
          </label>

          <label className="text-xs font-semibold uppercase tracking-[0.08em] text-brand-charcoal/86">
            Preferred counselling mode
            <select
              value={values.counsellingMode}
              onChange={(event) => setValues((prev) => ({ ...prev, counsellingMode: event.target.value }))}
              className={inputClassName}
            >
              <option value="">Select counselling mode</option>
              {counsellingModes.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {errors.counsellingMode ? <span className="mt-1 block text-xs text-red-600">{errors.counsellingMode}</span> : null}
          </label>

          <label className="text-xs font-semibold uppercase tracking-[0.08em] text-brand-charcoal/86 sm:col-span-2">
            Message
            <textarea
              rows={4}
              value={values.message}
              onChange={(event) => setValues((prev) => ({ ...prev, message: event.target.value }))}
              className={inputClassName}
              placeholder="Share your current stage and what clarity you are looking for"
            />
            {errors.message ? <span className="mt-1 block text-xs text-red-600">{errors.message}</span> : null}
          </label>
        </div>

        {status === "error" ? (
          <p className="mt-4 inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            <AlertCircle className="h-4 w-4" />
            {statusMessage || "Please review the highlighted fields and try again."}
          </p>
        ) : null}

        {status === "success" ? (
          <p className="mt-4 inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
            <CheckCircle2 className="h-4 w-4" />
            {statusMessage || "Thank you. Your enquiry was submitted successfully."}
          </p>
        ) : null}

        <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-brand-line/70 pt-5">
          <PrimaryButton
            className="bg-brand-gold text-brand-navy hover:bg-brand-gold-strong disabled:opacity-70"
            type="submit"
            disabled={status === "submitting"}
          >
            {status === "submitting" ? "Submitting..." : "Submit Enquiry"}
          </PrimaryButton>
          <p className="text-xs font-medium text-brand-charcoal/72">
            Every enquiry starts with a structured counselling discussion.
          </p>
        </div>
      </div>
    </form>
  );
}

export default ContactForm;
